import { useRef, useState } from 'react';
import { CheckIcon, ImageIcon, RefreshCwIcon, Trash2Icon, UploadIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toImageSrc } from '@/services/imageSrc';
import useClubErrors from '../hooks/useClubErrors';

const ACCEPTED_TYPES = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1000 * 1000;

/**
 * Upload de imagem do painel do clube.
 *
 * Confirmação acontece inline: dialogs aninhados em portais distintos ficavam
 * inertes por causa do focus trap do dialog que envolve o formulário.
 *
 * @param {boolean} [hidePreview] — esconde a caixa de prévia da imagem (útil quando o pai mostra outro preview, ex.: cartão).
 */
export default function ClubImageUpload({
    name,
    value,
    onChange,
    onPreviewChange,
    label,
    canvaWidth = 300,
    canvaHeight = 220,
    /** Prévia proporcional à largura do container (recomendação de px fica só no texto). */
    fluidPreview = false,
    /** Não renderiza a caixa de prévia da imagem — só ações de upload. */
    hidePreview = false,
    objectFit = 'cover',
    className,
    required = false,
}) {
    const inputRef = useRef(null);
    const { enqueueSnackbar } = useClubErrors();
    const [pending, setPending] = useState(null);
    const [confirmed, setConfirmed] = useState(null);
    const [dragging, setDragging] = useState(false);

    // Preserva o data URL com o mime real enquanto o form guarda só o base64.
    const currentSrc = confirmed?.base64 === value ? confirmed.dataUrl : toImageSrc(value);
    const displaySrc = pending?.dataUrl || currentSrc;
    const recommendedText =
        label ||
        (typeof canvaWidth === 'number'
            ? `Dimensões recomendadas: ${canvaWidth}x${canvaHeight}px. PNG ou JPEG.`
            : 'Formatos aceitos: PNG, JPEG ou WEBP (até 5MB).');

    const readFile = file => {
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            enqueueSnackbar('Formato não suportado. Use PNG, JPEG ou WEBP.', { variant: 'error' });
            return;
        }
        if (file.size > MAX_BYTES) {
            enqueueSnackbar('Arquivo maior que 5MB. Reduza a imagem e tente novamente.', { variant: 'error' });
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result);
            const next = { base64: dataUrl.replace(/^data:image\/[a-zA-Z+]+;base64,/, ''), dataUrl };
            setPending(next);
            // Atualiza preview externo (ex.: cartão) antes de confirmar.
            onPreviewChange?.(next.dataUrl);
        };
        reader.onerror = () => enqueueSnackbar('Não foi possível ler a imagem.', { variant: 'error' });
        reader.readAsDataURL(file);
    };

    const handleConfirm = () => {
        if (!pending) return;
        onChange?.(name, pending.base64);
        onPreviewChange?.(pending.dataUrl);
        setConfirmed(pending);
        setPending(null);
    };

    const handleCancel = () => {
        setPending(null);
        onPreviewChange?.(currentSrc || null);
    };

    const handleRemove = () => {
        setPending(null);
        setConfirmed(null);
        onChange?.(name, null);
        onPreviewChange?.(null);
    };

    const openPicker = () => inputRef.current?.click();

    const previewStyle = fluidPreview
        ? {
              width: '100%',
              height: 'auto',
              aspectRatio: `${canvaWidth} / ${canvaHeight}`,
          }
        : { width: canvaWidth, height: canvaHeight };

    return (
        <div className={cn('flex flex-col gap-3', fluidPreview || hidePreview ? 'w-full' : 'items-center', className)}>
            {!hidePreview ? (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={displaySrc ? undefined : openPicker}
                    onKeyDown={event => {
                        if (displaySrc) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openPicker();
                        }
                    }}
                    onDragOver={event => {
                        event.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={event => {
                        event.preventDefault();
                        setDragging(false);
                        readFile(event.dataTransfer.files?.[0]);
                    }}
                    className={cn(
                        'relative flex items-center justify-center overflow-hidden rounded-xl border bg-muted/30 transition-colors',
                        displaySrc ? 'border-solid border-border' : 'cursor-pointer border-2 border-dashed',
                        dragging && 'border-primary bg-primary/5',
                        !displaySrc && 'hover:border-primary/50 hover:bg-muted/60',
                    )}
                    style={previewStyle}
                >
                    {displaySrc ? (
                        <img
                            src={displaySrc}
                            alt="Pré-visualização"
                            className="size-full"
                            style={{ objectFit }}
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-2 px-4 text-center text-muted-foreground">
                            <ImageIcon className="size-7" />
                            <span className="text-xs">Clique ou arraste uma imagem</span>
                        </div>
                    )}
                </div>
            ) : null}

            <p className={cn('text-xs text-muted-foreground', fluidPreview || hidePreview ? 'text-left' : 'text-center')}>
                {recommendedText}
            </p>

            {pending ? (
                <div className={cn('flex flex-wrap gap-2', fluidPreview || hidePreview ? 'justify-start' : 'justify-center')}>
                    <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                        <XIcon className="size-4" />
                        Cancelar
                    </Button>
                    <Button type="button" size="sm" onClick={handleConfirm}>
                        <CheckIcon className="size-4" />
                        Confirmar imagem
                    </Button>
                </div>
            ) : (
                <div className={cn('flex flex-wrap gap-2', fluidPreview || hidePreview ? 'justify-start' : 'justify-center')}>
                    <Button type="button" variant="outline" size="sm" onClick={openPicker}>
                        {currentSrc ? <RefreshCwIcon className="size-4" /> : <UploadIcon className="size-4" />}
                        {currentSrc ? 'Trocar imagem' : 'Adicionar imagem'}
                    </Button>
                    {currentSrc && !required ? (
                        <Button type="button" variant="ghost" size="sm" onClick={handleRemove}>
                            <Trash2Icon className="size-4" />
                            Remover
                        </Button>
                    ) : null}
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                onChange={event => {
                    readFile(event.target.files?.[0]);
                    event.target.value = '';
                }}
            />
        </div>
    );
}
